import { useMemo, useState } from 'react';

import { enqueueSnackbar } from 'notistack';

import { CustomerSubscription } from '../interfaces/customerSubscription';
import { PromoCodeInfo } from '../interfaces/promoCodeInfo';
import { getIsNonPaidSubscription } from '../utils/getIsNonPaidSubscription/getIsNonPaidSubscription';
import { getFlagsObject } from '../../shared/utils/sortingAndGroupingUtil';
import {
  SUBSCRIPTION,
  SubscriptionTypeFlags,
} from '../../shared/constants/user-info/subscription';

type ApplyPromoCodeInfo = {
  subscriptions: CustomerSubscription[];
  promocode: PromoCodeInfo;
};

export type UseSelectSubscriptionsReturn = {
  subscriptions: CustomerSubscription[];
  discountPercent: number;
  selectedSubscription: CustomerSubscription | undefined;
  selectedSubscriptionTypeFlags: SubscriptionTypeFlags;
  applyPromoCode: (promoCode: string) => void;
  setSubscriptions: (subscriptions: CustomerSubscription[]) => void;
  setSelectedSubscriptionId: (selectedSubscriptionId: number) => void;
  setDiscountPercent: (discountPercent: number) => void;
  //We have two flags with a similar meaning, but they have different appointment.
  //isNonPaidSubscription flag is indicating that the subscription type which does not require payment (Free access or Self billing)
  isNonPaidSubscription: boolean;
  //isFreeSubscription flag is indicating that the subscription has 0 final prices (both initial and monthly)
  isFreeSubscription: boolean;
};

type UseSelectSubscriptionProps = {
  onApplyPromoCode: (promoCode: string) => Promise<ApplyPromoCodeInfo>;
};

export const useSelectSubscription = ({
  onApplyPromoCode,
}: UseSelectSubscriptionProps): UseSelectSubscriptionsReturn => {
  const [subscriptions, setSubscriptions] = useState<CustomerSubscription[]>([]);
  const [discountPercent, setDiscountPercent] = useState<number>(0);
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<number | null>(
    null,
  );

  const applyPromoCode = async (promoCode: string) => {
    const response = await onApplyPromoCode(promoCode);

    setSubscriptions(response.subscriptions);
    setDiscountPercent(response.promocode.discountPercent);

    enqueueSnackbar('Promo code successfully applied', {
      variant: 'success',
      autoHideDuration: 3000,
    });
  };

  const selectedSubscription = useMemo(
    () =>
      subscriptions.find((subscription) => subscription.id === selectedSubscriptionId),
    [selectedSubscriptionId, subscriptions],
  );

  const selectedSubscriptionTypeFlags = useMemo(
    () => getFlagsObject(SUBSCRIPTION, [selectedSubscription?.subscriptionType]),
    [selectedSubscription],
  );

  const isNonPaidSubscription = getIsNonPaidSubscription(
    selectedSubscription?.subscriptionType,
  );

  const isFreeSubscription =
    !selectedSubscription?.initialPlanPriceFinal &&
    !selectedSubscription?.monthlyRenewalPriceFinal;

  return {
    subscriptions,
    discountPercent,
    selectedSubscription,
    selectedSubscriptionTypeFlags,
    isNonPaidSubscription,
    isFreeSubscription,
    applyPromoCode,
    setSubscriptions,
    setSelectedSubscriptionId,
    setDiscountPercent,
  };
};
